// Step 3: the calculation on the device, its run and the results (plots, data and figures as a zip).
import { Alert, Button, Checkbox, Divider, Grid, Group, NumberInput, SegmentedControl, Select, Slider, Stack, Table, Tabs, Text, TextInput } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { notifyError } from "../../components/Errors";
import { Plot } from "../../components/Plot";
import { latestRun, PreviousRuns, RunCard } from "../../components/RunCard";
import { post, type Job, type Limits, type ProjectDetail } from "../../lib/api";
import { useLiveJob } from "../../lib/hooks";
import { CALCS, calcProblems, calcSpec, convergenceFigure, defaultElectrostatics, defaultScfIv, defaultTransferCurve, describeRun,
  esProblems, isScf, isSc, ivFigure, ldosFigure, negfStatus, profileFigure, profilesFigure, scfTransmissionFigure, siteMapFigure,
  transferCurveFigure, transmissionFigure,
  type Calc, type CalcForm, type NegfMemory } from "../../lib/negf";
import { fieldCutFigure, fieldMapFigure, leadBandsFigure } from "../../lib/fields";
import { ScCompare } from "./ScCompare";
import { EdgesOverlay } from "./EdgesOverlay";
import { absFigure, cbMapFigure, currentFigure, defaultAbs, defaultCb, icFigure, wireDosFigure, zeroBiasFigure, type AbsForm,
  type CbForm } from "../../lib/sc";
import { conductanceFigure, defaultSc, gapFigure, PLANES, scCutFigure, scMapFigure, SWEEP_VARIABLES, type Plane, type ScForm,
  type SweepForm, type SweepVariable } from "../../lib/sc";
import { useDeviceGeometry } from "./DeviceStep";
import { FieldsBlock } from "./FieldsBlock";

function Num({ label, value, onChange, unit, min, step, int = false }: {
  label: string; value: number; onChange: (v: number) => void; unit?: string; min?: number; step?: number; int?: boolean;
}) {
  return <NumberInput label={label} rightSection={unit ? <Text size="xs" c="dimmed">{unit}</Text> : undefined} rightSectionWidth={unit ? 44 : undefined}
    value={value} min={min} step={step} allowDecimal={!int} decimalScale={int ? 0 : 6}
    onChange={(v) => onChange(typeof v === "number" ? v : Number(v) || 0)} />;
}

function ScfResult({ r }: { r: Record<string, any> }) {
  const figs = useMemo(() => ({
    potential: siteMapFigure(r.positions, r.U, "Self-consistent potential energy U", "eV"),
    charge: siteMapFigure(r.positions, r.dn, "Induced charge (extra electrons per atom)", "e⁻", "PuOr"),
    profile: profileFigure(r),
    T: scfTransmissionFigure(r),
    conv: convergenceFigure(r.history ?? []),
    moments: r.moments ? siteMapFigure(r.positions, r.moments, "Magnetic moments (mean-field Hubbard U)", "μB", "RdBu") : null,
  }), [r]);
  const sc = r.scf ?? {};
  return (
    <Stack gap={4}>
      {!sc.converged && <Alert color="orange" p="xs">Not converged in {sc.iterations} iterations (last max |ΔU| {sc.max_dU_meV} meV):
        these are the last iteration's results. Try more iterations, a smaller mixing, or a smaller gate voltage.</Alert>}
      <Tabs defaultValue="potential">
        <Tabs.List>
          <Tabs.Tab value="potential">Potential</Tabs.Tab>
          <Tabs.Tab value="charge">Charge</Tabs.Tab>
          {figs.moments && <Tabs.Tab value="moments">Moments</Tabs.Tab>}
          <Tabs.Tab value="profile">Profile</Tabs.Tab>
          <Tabs.Tab value="T">T(E)</Tabs.Tab>
          <Tabs.Tab value="conv">Convergence</Tabs.Tab>
        </Tabs.List>
        {(["potential", "charge", "profile", "T", "conv"] as const).map((k) => (
          <Tabs.Panel key={k} value={k}><Plot data={figs[k].data} layout={figs[k].layout} height={400} /></Tabs.Panel>
        ))}
        {figs.moments && <Tabs.Panel value="moments"><Plot data={figs.moments.data} layout={figs.moments.layout} height={400} /></Tabs.Panel>}
      </Tabs>
      <Text size="xs" c="dimmed">{sc.converged ? "Converged" : "Stopped"} after {sc.iterations} iterations ({sc.seconds} s) ·
        induced charge {(-(sc.induced_electrons ?? 0)).toFixed(4)} e · U from {sc.U_min} to {sc.U_max} eV ·
        device {r.device?.sites} atoms, Poisson grid {r.device?.poisson_grid?.join(" × ")}
        {r.hubbard && <> · Hubbard U: largest moment {r.hubbard.max_moment} μB, total {r.hubbard.total_moment} μB
          {r.hubbard.lead_magnetism ? `, leads ${r.hubbard.leads?.[0]?.max_moment} μB` : ", non-magnetic leads"}</>}</Text>
    </Stack>
  );
}

function ScfIvResult({ r }: { r: Record<string, any> }) {
  const figs = useMemo(() => ({ iv: ivFigure({ ...r, mode: "self-consistent" }), profiles: profilesFigure(r) }), [r]);
  return (
    <Tabs defaultValue="iv">
      <Tabs.List>
        <Tabs.Tab value="iv">I–V</Tabs.Tab>
        <Tabs.Tab value="profiles">Potential profiles</Tabs.Tab>
        <Tabs.Tab value="conv">Convergence</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="iv"><Plot data={figs.iv.data} layout={figs.iv.layout} height={400} /></Tabs.Panel>
      <Tabs.Panel value="profiles"><Plot data={figs.profiles.data} layout={figs.profiles.layout} height={400} /></Tabs.Panel>
      <Tabs.Panel value="conv">
        <Table fz="xs" mt="xs" data-testid="scf-iv-table">
          <Table.Thead><Table.Tr><Table.Th>Bias (V)</Table.Th><Table.Th>Converged</Table.Th><Table.Th>Iterations</Table.Th>
            <Table.Th>max |ΔU| (meV)</Table.Th>{r.moments_by_bias && <Table.Th>max |m| (μB)</Table.Th>}<Table.Th>I (µA)</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {(r.scf as any[]).map((x, i) => (
              <Table.Tr key={i}><Table.Td>{x.bias}</Table.Td><Table.Td>{x.converged ? "yes" : "no"}</Table.Td><Table.Td>{x.iterations}</Table.Td>
                <Table.Td>{x.max_dU_meV}</Table.Td>{r.moments_by_bias && <Table.Td>{x.max_moment}</Table.Td>}
                <Table.Td>{(r.I[i] * 1e6).toPrecision(4)}</Table.Td></Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Tabs.Panel>
    </Tabs>
  );
}

function TransferCurveResult({ r }: { r: Record<string, any> }) {
  const fig = useMemo(() => transferCurveFigure(r), [r]);
  const sw = r.subthreshold_swing_V_per_dec;
  const swStr = sw == null || !isFinite(sw) ? "n/a" : `${(sw * 1000).toFixed(1)} mV/dec`;
  return (
    <Stack gap={4}>
      <Plot data={fig.data} layout={fig.layout} height={420} />
      <Text size="xs" c="dimmed">
        {r.Vg_V?.length} gate points · Vds = {r.Vds_V} V · T = {(r.kT_eV / 8.617333262e-5).toFixed(0)} K ·
        SW = {swStr} · {r.seconds} s · Kwant {r.versions?.kwant}
      </Text>
    </Stack>
  );
}

function FieldSweepResult({ r }: { r: Record<string, any> }) {
  const [i, setI] = useState(0);
  const map = useMemo(() => fieldMapFigure(r), [r]);
  const cut = useMemo(() => fieldCutFigure(r, Math.min(i, r.B.length - 1)), [r, i]);
  return (
    <Tabs defaultValue="map">
      <Tabs.List>
        <Tabs.Tab value="map">T(B, E) map</Tabs.Tab>
        <Tabs.Tab value="cut">T(E) at one field</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="map"><Plot data={map.data} layout={map.layout} height={420} /></Tabs.Panel>
      <Tabs.Panel value="cut">
        <Plot data={cut.data} layout={cut.layout} height={380} />
        <Slider mt="xs" min={0} max={r.B.length - 1} step={1} value={i} onChange={setI}
          label={(v) => `${(+r.B[v]).toPrecision(4)} T`} data-testid="field-slider" />
      </Tabs.Panel>
      <Text size="xs" c="dimmed" mt={4}>{r.B.length} fields × {r.E.length} energies · {r.seconds} s ·
        Kwant {r.versions?.kwant}{r.spin_degeneracy === 2 ? " · spin-degenerate (×2 in G)" : ""}</Text>
    </Tabs>
  );
}

function ScConductanceResult({ r }: { r: Record<string, any> }) {
  const fig = useMemo(() => conductanceFigure(r), [r]);
  const dos = useMemo(() => (r.dos ? wireDosFigure(r) : null), [r]);
  return (
    <Tabs defaultValue="G">
      <Tabs.List>
        <Tabs.Tab value="G">G(V)</Tabs.Tab>
        {dos && <Tabs.Tab value="dos">Wire DOS</Tabs.Tab>}
        <Tabs.Tab value="compare">Compare with measurement</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="G"><Plot data={fig.data} layout={fig.layout} height={400} /></Tabs.Panel>
      {dos && <Tabs.Panel value="dos"><Plot data={dos.data} layout={dos.layout} height={360} /></Tabs.Panel>}
      <Tabs.Panel value="compare"><ScCompare r={r} /></Tabs.Panel>
      <ScFootnote r={r} />
    </Tabs>
  );
}

function AbsResult({ r }: { r: Record<string, any> }) {
  const figs = useMemo(() => (Array.isArray(r.X) ? { ic: icFigure(r) } : { levels: absFigure(r), current: currentFigure(r) }), [r]);
  return (
    <Stack gap={4}>
      {figs.ic && <Plot data={figs.ic.data} layout={figs.ic.layout} height={400} />}
      {figs.levels && <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}><Plot data={figs.levels.data} layout={figs.levels.layout} height={380} /></Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}><Plot data={figs.current!.data} layout={figs.current!.layout} height={380} /></Grid.Col>
      </Grid>}
      <Text size="xs" c="dimmed">Closed S–wire–S wire (no leads): {r.device?.sites} atoms, {r.device?.orbitals} BdG orbitals · the phase of contact
        {" "}{(r.contact ?? 0) + 1} advances · I = (2e/ħ) dF/dφ at T = {r.temperature_K} K (all levels, from the Hellmann–Feynman theorem) ·
        {" "}{r.seconds} s. The superconductors must be longer than their coherence length (ħv_F/Δ) for the levels below Δ to be converged.</Text>
    </Stack>
  );
}

function ScLdosResult({ r }: { r: Record<string, any> }) {
  const fig = useMemo(() => {
    const f = ldosFigure({ ...r, energy: r.energy_meV });
    return { data: f.data, layout: { ...f.layout, title: { text: `${r.name || "Electron BdG LDOS"} at E = ${r.energy_meV} meV` } } };
  }, [r]);
  return (
    <Stack gap={4}>
      <Plot data={fig.data} layout={fig.layout} height={400} />
      <Text size="xs" c="dimmed">Electron part of the BdG local density of states (states / eV) on each atom at E = {r.energy_meV} meV, with
        the leads attached · {r.seconds} s. A Majorana zero mode shows as weight at the ends of a topological wire.</Text>
    </Stack>
  );
}

function ScFootnote({ r }: { r: Record<string, any> }) {
  const gaps = Array.isArray(r.contact_gaps_meV?.[0]) ? null : (r.contact_gaps_meV as number[] | undefined);
  return (
    <>
      {(r.notes ?? []).map((n: string) => <Text key={n} size="xs" c="orange">{n}</Text>)}
      <Text size="xs" c="dimmed">{r.method === "coulomb"
        ? `Coulomb map at n_g = ${(+r.n_g).toFixed(4)} (fixed gate): ${r.island === "dense" ? "dense-level island" : "discrete levels"}, E_C = ${r.E_C_meV} meV`
        : r.method === "tunnel"
        ? `Tunnel spectroscopy: contact ${(r.probe_contact ?? 0) + 1} into ${r.wire === "bcs" ? "a second BCS superconductor (S–I–S)" : `the wire (${r.device?.tunnel_sites} sites of a ${r.device?.sites}-atom BdG device)`}, G_N = ${r.G_N} × 2e²/h`
        : `BdG device: ${r.device?.sites} atoms, ${r.device?.orbitals} orbitals (electrons and holes) · probe: the ${r.probe === 0 ? "left" : "right"} lead`}{gaps ? ` · Δ of the contacts ${gaps.join(", ")} meV` : ""}
        {typeof r.wire_gap_meV === "number" ? ` · wire gap ${r.wire_gap_meV.toPrecision(4)} meV` : ""} · {r.seconds} s · Kwant {r.versions?.kwant}</Text>
    </>
  );
}

function ScMapResult({ r, projectId }: { r: Record<string, any>; projectId?: string | null }) {
  const [i, setI] = useState(0);
  const map = useMemo(() => scMapFigure(r), [r]);
  const cut = useMemo(() => scCutFigure(r, Math.min(i, r.X.length - 1)), [r, i]);
  const gaps = useMemo(() => gapFigure(r), [r]);
  return (
    <Tabs defaultValue="map">
      <Tabs.List>
        <Tabs.Tab value="map">G(V, {r.variable}) map</Tabs.Tab>
        <Tabs.Tab value="cut">G(V) at one {r.variable}</Tabs.Tab>
        <Tabs.Tab value="gaps">Gaps</Tabs.Tab>
        <Tabs.Tab value="compare">Compare with measurement</Tabs.Tab>
        <Tabs.Tab value="edges">Measured edges</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="map"><Plot data={map.data} layout={map.layout} height={440} /></Tabs.Panel>
      <Tabs.Panel value="edges"><EdgesOverlay r={r} projectId={projectId} /></Tabs.Panel>
      <Tabs.Panel value="cut">
        <Plot data={cut.data} layout={cut.layout} height={380} />
        <Slider mt="xs" min={0} max={r.X.length - 1} step={1} value={i} onChange={setI}
          label={(v) => `${(+r.X[v]).toPrecision(4)} ${r.unit}`} data-testid="sc-slider" />
      </Tabs.Panel>
      <Tabs.Panel value="gaps"><Plot data={gaps.data} layout={gaps.layout} height={360} /></Tabs.Panel>
      <Tabs.Panel value="compare"><ScCompare r={r} /></Tabs.Panel>
      <ScFootnote r={r} />
      <Text size="xs" c="dimmed">{r.X.length} × {r.V.length} points{r.method === "tunnel" ? ` · tunnel spectroscopy at ${r.temperature_K} K` : r.temperature_K > 0 ? `, smeared at ${r.temperature_K} K` : ", T = 0"}.</Text>
    </Tabs>
  );
}

function ScForms({ sc, set, limits }: { sc: ScForm; set: (n: Partial<ScForm>) => void; limits: Limits }) {
  const b = sc.bias;
  return (
    <>
      <Group grow>
        <Num label="Bias from" unit="mV" step={0.1} value={b.min_mV} onChange={(v) => set({ bias: { ...b, min_mV: v } })} />
        <Num label="to" unit="mV" step={0.1} value={b.max_mV} onChange={(v) => set({ bias: { ...b, max_mV: v } })} />
      </Group>
      <Num label="Bias points" int min={2} value={b.n} onChange={(v) => set({ bias: { ...b, n: v } })} />
      <Text size="xs" c="dimmed">The bias V is the energy eV of the electrons from the probe (the superconductor grounded). Contacts,
        field, g-tensor, gate and temperature: step 4. Your plan: up to {String(limits["negf.max_energy_points"])} bias points.</Text>
    </>
  );
}

function CbForms({ sc, set, limits }: { sc: ScForm; set: (n: Partial<ScForm>) => void; limits: Limits }) {
  const c = sc.cb ?? defaultCb();
  const setC = (n: Partial<CbForm>) => set({ cb: { ...c, ...n } });
  const b = sc.bias;
  const dense = (c.island ?? "levels") === "dense", field = (c.mode ?? "gate") === "field";
  return (
    <>
      <SegmentedControl size="xs" fullWidth value={c.island ?? "levels"} onChange={(v) => setC({ island: v as CbForm["island"] })} data-testid="cb-island"
        data={[{ value: "levels", label: "Discrete levels (a short dot)" }, { value: "dense", label: "Dense levels (a long wire: metallic island)" }]} />
      <SegmentedControl size="xs" fullWidth value={c.mode ?? "gate"} onChange={(v) => {
        const mode = v as CbForm["mode"];
        setC({ mode });
        if (mode === "field" && sc.sweep.variable !== "B" && sc.sweep.variable !== "angle") set({ sweep: { ...sc.sweep, variable: "B", min: 0, max: 2, n: 21 } });
      }} data-testid="cb-mode"
        data={[{ value: "gate", label: "Over the gate: G(V_g, V)" }, { value: "field", label: "Over the field at a fixed gate: G(V, B or angle)" }]} />
      {dense ? (
        <Group grow>
          <Num label="Junction G_L" unit="2e²/h" min={0} step={0.05} value={c.G_L ?? 0.3} onChange={(v) => setC({ G_L: v })} />
          <Num label="Junction G_R" unit="2e²/h" min={0} step={0.05} value={c.G_R ?? 0.3} onChange={(v) => setC({ G_R: v })} />
        </Group>
      ) : <>
      <SegmentedControl size="xs" fullWidth value={c.levels} onChange={(v) => setC({ levels: v as CbForm["levels"] })}
        data={[{ value: "model", label: "Levels of the wire (model)" }, { value: "manual", label: "Levels entered" }]} />
      {c.levels === "model"
        ? <Group grow>
            <Num label="Levels nearest μ" int min={1} value={c.n_levels} onChange={(v) => setC({ n_levels: v })} />
            <Select label="Tunnel rates" allowDeselect={false} value={c.rates} onChange={(v) => setC({ rates: (v ?? "constant") as CbForm["rates"] })}
              data={[{ value: "constant", label: "the same for every level" }, { value: "wavefunction", label: "from the wavefunctions at the ends" }]} />
          </Group>
        : <TextInput label="Levels (meV; a spin-degenerate level twice)" value={c.manual_meV.join(", ")}
            onChange={(e) => setC({ manual_meV: e.currentTarget.value.split(/[ ,;]+/).filter((x) => x.trim() !== "").map(Number).filter(Number.isFinite) })} />}
      </>}
      {field
        ? <Num label="Gate V_g (fixed)" unit="mV" value={c.gate_fixed_mV ?? 0} onChange={(v) => setC({ gate_fixed_mV: v })} />
        : <Group grow>
            <Num label="V_g from" unit="mV" value={c.gate_mV.min} onChange={(v) => setC({ gate_mV: { ...c.gate_mV, min: v } })} />
            <Num label="to" unit="mV" value={c.gate_mV.max} onChange={(v) => setC({ gate_mV: { ...c.gate_mV, max: v } })} />
            <Num label="points" int min={2} value={c.gate_mV.n} onChange={(v) => setC({ gate_mV: { ...c.gate_mV, n: v } })} />
          </Group>}
      <Group grow>
        <Num label="Bias from" unit="mV" step={0.1} value={b.min_mV} onChange={(v) => set({ bias: { ...b, min_mV: v } })} />
        <Num label="to" unit="mV" step={0.1} value={b.max_mV} onChange={(v) => set({ bias: { ...b, max_mV: v } })} />
        <Num label="points" int min={2} value={b.n} onChange={(v) => set({ bias: { ...b, n: v } })} />
      </Group>
      <Group grow>
        <Num label="Lever arm α" step={0.01} min={0} value={c.lever_arm} onChange={(v) => setC({ lever_arm: v })} />
        {!dense && <Num label="Γ_L" unit="µeV" min={0} value={c.gamma_L_ueV} onChange={(v) => setC({ gamma_L_ueV: v })} />}
        {!dense && <Num label="Γ_R" unit="µeV" min={0} value={c.gamma_R_ueV} onChange={(v) => setC({ gamma_R_ueV: v })} />}
      </Group>
      <Group grow>
        <Num label="Charge offset n₀" step={0.1} value={c.n0} onChange={(v) => setC({ n0: v })} />
        <Num label="Dynes γ of the leads" unit="meV" min={0} step={0.005} value={c.dynes_meV} onChange={(v) => setC({ dynes_meV: v })} />
        <Num label="Bias split η" min={0} step={0.01} value={c.eta ?? 0.5} onChange={(v) => setC({ eta: v })} />
      </Group>
      <Checkbox label="Field-dependent gate charge: n_g(B) = n_g + BᵀQB" checked={!!c.gate_Q_per_T2} data-testid="cb-gate-q"
        onChange={(e) => setC({ gate_Q_per_T2: e.currentTarget.checked ? [[0, 0, 0], [0, 0, 0], [0, 0, 0]] : null })} />
      {c.gate_Q_per_T2 && <GateQ Q={c.gate_Q_per_T2} set={(Q) => setC({ gate_Q_per_T2: Q })} />}
      <Text size="xs" c="dimmed">Constant interaction: E = Σ ε + (E_C/2)(N − n_g)², n_g = α V_g / E_C + n₀, with E_C = {sc.charging_energy_meV} meV (step 4);
        sequential tunnelling to the left and right leads of step 4 (normal, or superconducting with their Δ(B, T): the diamonds open by
        2Δ per superconductor). The model's levels include the field and g-tensor of step 4. Dense levels: a long wire (level spacing ≪ E_C)
        charges as a metallic island (orthodox theory) - the strip of blockade at a fixed gate stays above the contacts' critical field, and G
        tends to the junctions' series conductance; the island's Zeeman splitting does not move its thresholds. Over the field: the sweep below,
        n_g = α V_g / E_C + n₀ at the fixed gate. Bias split η: μ_L = η V, μ_R = −(1 − η) V (0.5 = symmetric; unequal junction capacitances
        divide the bias unevenly, and the upper and lower edges then move by different amounts). The field-dependent gate charge BᵀQB (e per T², B in the
        wire's frame) adds to n_g at each field: the field shifts the island's chemical potential like a small gate voltage. Your plan: up to {String(limits["negf.max_energy_points"])} gate and bias points each.</Text>
    </>
  );
}

/** The symmetric 3 × 3 matrix Q (e per T²) of the field-dependent gate charge: editing one entry sets its mirror too. */
function GateQ({ Q, set }: { Q: number[][]; set: (Q: number[][]) => void }) {
  const ax = ["x", "y", "z"];
  return (
    <Grid gutter={4} data-testid="cb-gate-q-matrix">
      {[0, 1, 2].map((a) => [0, 1, 2].map((b) => (
        <Grid.Col span={4} key={`${a}${b}`}>
          <NumberInput size="xs" label={a === 0 ? `Q·${ax[b]}` : undefined} leftSection={b === 0 ? <Text size="xs">{ax[a]}</Text> : undefined}
            step={0.01} decimalScale={6} value={Q[a][b]} disabled={b < a}
            onChange={(v) => {
              const x = typeof v === "number" ? v : Number(v) || 0;
              set(Q.map((row, i) => row.map((q, j) => ((i === a && j === b) || (i === b && j === a) ? x : q))));
            }} />
        </Grid.Col>
      )))}
    </Grid>
  );
}

function CbResult({ r, projectId }: { r: Record<string, any>; projectId?: string | null }) {
  const map = useMemo(() => cbMapFigure(r), [r]);
  const zb = useMemo(() => zeroBiasFigure(r), [r]);
  return (
    <Tabs defaultValue="map">
      <Tabs.List>
        <Tabs.Tab value="map">G(V_g, V)</Tabs.Tab>
        <Tabs.Tab value="zb">Zero bias</Tabs.Tab>
        {(r.levels_meV ?? []).length > 0 && <Tabs.Tab value="levels">Levels</Tabs.Tab>}
        <Tabs.Tab value="edges">Measured edges</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="edges"><EdgesOverlay r={r} projectId={projectId} /></Tabs.Panel>
      <Tabs.Panel value="map"><Plot data={map.data} layout={map.layout} height={440} /></Tabs.Panel>
      <Tabs.Panel value="zb"><Plot data={zb.data} layout={zb.layout} height={380} /></Tabs.Panel>
      <Tabs.Panel value="levels">
        <Table fz="xs" mt="xs" data-testid="cb-levels">
          <Table.Thead><Table.Tr><Table.Th>#</Table.Th><Table.Th>ε (meV)</Table.Th>{r.level_weights && <><Table.Th>weight left</Table.Th><Table.Th>weight right</Table.Th></>}</Table.Tr></Table.Thead>
          <Table.Tbody>
            {(r.levels_meV as number[]).map((e, i) => (
              <Table.Tr key={i}><Table.Td>{i + 1}</Table.Td><Table.Td>{e.toPrecision(5)}</Table.Td>
                {r.level_weights && <><Table.Td>{r.level_weights.left[i].toPrecision(3)}</Table.Td><Table.Td>{r.level_weights.right[i].toPrecision(3)}</Table.Td></>}</Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Tabs.Panel>
      {(r.notes ?? []).map((n: string) => <Text key={n} size="xs" c="dimmed">{n}</Text>)}
      <Text size="xs" c="dimmed">E_C = {r.E_C_meV} meV · lever arm {r.lever_arm} · addition energies {(r.addition_meV ?? []).map((x: number) => x.toFixed(3)).join(", ")} meV
        (the diamonds' heights) · lead gaps {(r.lead_gaps_meV ?? []).join(", ")} meV · {r.seconds} s.</Text>
    </Tabs>
  );
}

function AbsForms({ sc, set }: { sc: ScForm; set: (n: Partial<ScForm>) => void }) {
  const a = sc.abs ?? defaultAbs();
  const setA = (n: Partial<AbsForm>) => set({ abs: { ...a, ...n } });
  return (
    <>
      <Select label="The phase of" allowDeselect={false} value={String(a.contact)} onChange={(v) => setA({ contact: Number(v ?? 1) })}
        data={sc.contacts.map((c, i) => ({ value: String(i), label: c.name || `contact ${i + 1}` }))} />
      <Group grow>
        <Num label="φ from" unit="°" value={a.phase_min_deg} onChange={(v) => setA({ phase_min_deg: v })} />
        <Num label="to" unit="°" value={a.phase_max_deg} onChange={(v) => setA({ phase_max_deg: v })} />
        <Num label="phases" int min={2} value={a.n} onChange={(v) => setA({ n: v })} />
      </Group>
      <Num label="Levels shown" int min={1} value={a.levels} onChange={(v) => setA({ levels: v })} />
      <Checkbox label="Critical current against a sweep (field, angle, gate or μ)" checked={a.map} onChange={(e) => setA({ map: e.currentTarget.checked })} />
      <Text size="xs" c="dimmed">The closed wire of step 2 (no leads) with the two superconducting regions of step 4: Andreev levels E(φ) and the
        supercurrent I(φ) as the phase of one contact advances; the temperature of step 4 sets the occupations.</Text>
    </>
  );
}

function SweepForms({ sc, set, calc, limits }: { sc: ScForm; set: (n: Partial<ScForm>) => void; calc: Calc; limits: Limits }) {
  const w = sc.sweep;
  const setW = (n: Partial<SweepForm>) => set({ sweep: { ...w, ...n } });
  const cbField = calc === "cb_map" && (sc.cb?.mode ?? "gate") === "field";
  if (calc !== "sc_map" && !cbField && !(calc === "sc_abs" && (sc.abs ?? defaultAbs()).map)) return null;
  const unit = SWEEP_VARIABLES.find((v) => v.value === w.variable)?.unit ?? "";
  return (
    <>
      <Select label="Sweep" allowDeselect={false} value={w.variable}
        data={SWEEP_VARIABLES.filter((v) => !cbField || v.value === "B" || v.value === "angle").map((v) => ({ value: v.value, label: v.label }))}
        onChange={(v) => {
          const variable = (v ?? "B") as SweepVariable;
          const range = variable === "angle" ? { min: 0, max: 360, n: 37 } : variable === "B" ? { min: 0, max: 2, n: 21 } : { min: -50, max: 50, n: 21 };
          setW({ variable, ...range });
        }} data-testid="sc-sweep" />
      <Group grow>
        <Num label="from" unit={unit} value={w.min} onChange={(v) => setW({ min: v })} />
        <Num label="to" unit={unit} value={w.max} onChange={(v) => setW({ max: v })} />
        <Num label="points" int min={2} value={w.n} onChange={(v) => setW({ n: v })} />
      </Group>
      {w.variable === "B" && (
        <>
          <Select label="Direction" allowDeselect={false} value={w.direction} onChange={(v) => setW({ direction: (v ?? "z") as SweepForm["direction"] })}
            data={[{ value: "z", label: "along z (the wire)" }, { value: "y", label: "along y" }, { value: "x", label: "along x" },
              { value: "angles", label: "at angles θ, φ" }]} />
          {w.direction === "angles" && <Group grow>
            <Num label="θ (from X)" unit="°" value={w.theta_deg} onChange={(v) => setW({ theta_deg: v })} />
            <Num label="φ (from Z)" unit="°" value={w.phi_deg} onChange={(v) => setW({ phi_deg: v })} />
          </Group>}
        </>
      )}
      {w.variable === "angle" && (
        <>
          <Select label="Plane" allowDeselect={false} value={w.plane} onChange={(v) => setW({ plane: (v ?? "XY") as Plane })}
            data={PLANES.map((p) => ({ value: p.value, label: p.label }))} />
          <Num label="|B|" unit="T" min={0} step={0.1} value={w.B_T} onChange={(v) => setW({ B_T: v })} />
          <Text size="xs" c="dimmed">{PLANES.find((p) => p.value === w.plane)?.about}.</Text>
        </>
      )}
      {w.variable === "gate" && <Text size="xs" c="dimmed">The gate of step 4 ({sc.gate.cells ? `cells ${sc.gate.cells[0]}…${sc.gate.cells[1] - 1}` : "the whole device"}) takes each value.</Text>}
      {calc === "sc_map" && (sc.method ?? "coherent") === "coherent" && <Checkbox label="Also the proximitized wire's BdG gap at each point (topological: it closes and reopens)" checked={sc.gap}
        onChange={(e) => set({ gap: e.currentTarget.checked })} />}
      <Text size="xs" c="dimmed">One device; the field, gate or μ changes from point to point. Your plan: up to
        {" "}{String(limits["negf.max_field_points"])} sweep points and {String(limits["negf.sc_max_map_points"] ?? "?")} points in a map.</Text>
    </>
  );
}

export function NegfResult({ job }: { job: Job }) {
  const r = job.result;
  const fig = useMemo(() => {
    if (job.status !== "succeeded" || !r || ["scf", "scf_iv", "field_sweep", "transfer_curve", "sc_conductance", "sc_map", "sc_abs", "sc_ldos", "cb_map"].includes(r.calculation)) return null;
    if (r.calculation === "iv") return ivFigure(r);
    if (r.calculation === "ldos") return ldosFigure(r);
    if (r.calculation === "lead_bands") return leadBandsFigure(r);
    return transmissionFigure(r);
  }, [job.status, r]);
  if (job.status === "succeeded" && r?.calculation === "scf") return <ScfResult r={r} />;
  if (job.status === "succeeded" && r?.calculation === "scf_iv") return <ScfIvResult r={r} />;
  if (job.status === "succeeded" && r?.calculation === "field_sweep") return <FieldSweepResult r={r} />;
  if (job.status === "succeeded" && r?.calculation === "transfer_curve") return <TransferCurveResult r={r} />;
  if (job.status === "succeeded" && r?.calculation === "sc_conductance") return <ScConductanceResult r={r} />;
  if (job.status === "succeeded" && r?.calculation === "sc_abs") return <AbsResult r={r} />;
  if (job.status === "succeeded" && r?.calculation === "sc_ldos") return <ScLdosResult r={r} />;
  if (job.status === "succeeded" && r?.calculation === "cb_map") return r.mode === "field" ? <ScMapResult r={r} projectId={job.project_id} /> : <CbResult r={r} projectId={job.project_id} />;
  if (job.status === "succeeded" && r?.calculation === "sc_map") return <ScMapResult r={r} projectId={job.project_id} />;
  if (!fig || !r) return null;
  return (
    <Stack gap={4}>
      <Plot data={fig.data} layout={fig.layout} height={400} />
      <Text size="xs" c="dimmed">Device: {r.device?.sites} atoms, {r.device?.orbitals} orbitals · {r.seconds} s ·
        Kwant {r.versions?.kwant}{r.spin_degeneracy === 2 ? " · spin-degenerate (×2 in G and I)" : ""}</Text>
    </Stack>
  );
}

export function CalcStep({ project, mem, update, remember, limits, onBack }: {
  project: ProjectDetail; mem: NegfMemory; update: (m: Partial<NegfMemory>) => void; remember: (m: Partial<NegfMemory>) => void;
  limits: Limits; onBack: () => void;
}) {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["negf-status"], queryFn: negfStatus, staleTime: 60_000 });
  const geo = useDeviceGeometry(project, mem);
  const calc = mem.calc;
  const f = mem.form;
  const setForm = (next: Partial<CalcForm>) => update({ form: { ...f, ...next } });
  const kind = `negf.${calc}`;
  const shownId = mem.jobs?.[calc] ?? latestRun(project, kind)?.id;
  const { job } = useLiveJob(shownId);
  const s = mem.summary;
  const g = geo.data;
  const sc = mem.sc ?? defaultSc(s?.species ?? []);
  const setSc = (next: Partial<ScForm>) => update({ sc: { ...sc, ...next } });
  const problems = calcProblems(calc, f, 0, g?.transport_vector ? [g.transport_vector] : undefined, sc, mem.device.length);
  if (g?.within_limit === false) problems.push("The device is larger than your plan allows (step 2).");
  const es = mem.electrostatics ?? defaultElectrostatics();
  const scfMax = Number(limits["negf.scf_max_device_orbitals"] ?? 0);
  if (isScf(calc)) {
    if (scfMax && (g?.orbitals ?? 0) > scfMax)
      problems.push(`Self-consistent runs on your plan take devices up to ${scfMax.toLocaleString()} orbitals (step 2).`);
    problems.push(...esProblems(es));
  }
  const sv = f.scf_iv ?? defaultScfIv();

  const run = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind, project_id: project.id, params: { model_file: mem.model_file, spec: calcSpec(mem.device, calc, f, es, sc) } }),
    onSuccess: (j) => {
      remember(isSc(calc) ? { jobs: { ...(mem.jobs ?? {}), [calc]: j.id }, sc } : { jobs: { ...(mem.jobs ?? {}), [calc]: j.id } });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => notifyError(e),
  });

  return (
    <Grid mt="md" gutter="lg">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="sm">
          <SegmentedControl fullWidth size="xs" value={CALCS.slice(0, 5).some((c) => c.value === calc) ? calc : ""}
            onChange={(v) => update({ calc: v as Calc })} data={CALCS.slice(0, 5)} data-testid="calc" />
          <SegmentedControl fullWidth size="xs" value={CALCS.slice(5, 8).some((c) => c.value === calc) ? calc : ""}
            onChange={(v) => update({ calc: v as Calc })} data={CALCS.slice(5, 8)} data-testid="calc-fields" />
          <Select size="xs" placeholder="Superconducting contacts and charging…" value={CALCS.slice(8).some((c) => c.value === calc) ? calc : null}
            onChange={(v) => v && update({ calc: v as Calc })} data={CALCS.slice(8)} data-testid="calc-sc" allowDeselect={false} />
          <TextInput label="Name (shown on the plots)" placeholder="e.g. zigzag ribbon, 8 chains" value={f.name}
            onChange={(e) => setForm({ name: e.currentTarget.value })} />
          {calc === "transmission" && (
            <>
              <Group grow>
                <Num label="E min" unit="eV" value={f.energies.min} step={0.1} onChange={(v) => setForm({ energies: { ...f.energies, min: v } })} />
                <Num label="E max" unit="eV" value={f.energies.max} step={0.1} onChange={(v) => setForm({ energies: { ...f.energies, max: v } })} />
              </Group>
              <Num label="Energy points" int min={2} value={f.energies.n} onChange={(v) => setForm({ energies: { ...f.energies, n: v } })} />
              <Text size="xs" c="dimmed">Energies relative to the Fermi level of the fit. Your plan: up to {String(limits["negf.max_energy_points"])} points.</Text>
              {s?.spin_resolved_possible && (
                <Select label="Spin" data={[{ value: "auto", label: "Resolved: T↑, T↓ and total" }, { value: "off", label: "Total only" }]}
                  value={f.spin} onChange={(v) => setForm({ spin: (v ?? "auto") as CalcForm["spin"] })} allowDeselect={false} />
              )}
            </>
          )}
          {calc === "iv" && (
            <>
              <Group grow>
                <Num label="Bias from" unit="V" value={f.iv.bias_min} step={0.1} onChange={(v) => setForm({ iv: { ...f.iv, bias_min: v } })} />
                <Num label="to" unit="V" value={f.iv.bias_max} step={0.1} onChange={(v) => setForm({ iv: { ...f.iv, bias_max: v } })} />
              </Group>
              <Num label="Bias points" int min={2} value={f.iv.n} onChange={(v) => setForm({ iv: { ...f.iv, n: v } })} />
              <Group grow>
                <Num label="Temperature" unit="K" min={0} value={f.iv.temperature_K} onChange={(v) => setForm({ iv: { ...f.iv, temperature_K: v } })} />
                <Num label="Energy step dE" unit="eV" min={0} step={0.001} value={f.iv.dE} onChange={(v) => setForm({ iv: { ...f.iv, dE: v } })} />
              </Group>
              <Num label="Fermi level shift μ" unit="eV" step={0.05} value={f.iv.mu} onChange={(v) => setForm({ iv: { ...f.iv, mu: v } })} />
              <Select label="Potential drop" allowDeselect={false} value={f.iv.mode}
                onChange={(v) => setForm({ iv: { ...f.iv, mode: (v ?? "rigid") as CalcForm["iv"]["mode"] } })}
                data={[{ value: "rigid", label: "Rigid band (T at zero bias)" }, { value: "linear", label: "Linear drop across the central region" }]} />
              <Text size="xs" c="dimmed">Landauer current, symmetric bias μ ± V/2. Not self-consistent: the self-consistent
                (Poisson) potential comes in a later version. Your plan: up to {String(limits["negf.max_bias_points"])} bias points.</Text>
            </>
          )}
          {calc === "scf" && (
            <>
              <Text size="xs" c="dimmed">The potential and charge with the gates of step 3 ({es.gates.length
                ? es.gates.map((x) => `${x.name} ${x.voltage} V`).join(", ") : "no gates"}; μ = {es.mu} eV, {es.temperature_K} K),
                then T(E) through that potential next to the flat-band T(E).</Text>
              <Group grow>
                <Num label="E min" unit="eV" value={f.energies.min} step={0.1} onChange={(v) => setForm({ energies: { ...f.energies, min: v } })} />
                <Num label="E max" unit="eV" value={f.energies.max} step={0.1} onChange={(v) => setForm({ energies: { ...f.energies, max: v } })} />
              </Group>
              <Num label="Energy points" int min={2} value={f.energies.n} onChange={(v) => setForm({ energies: { ...f.energies, n: v } })} />
            </>
          )}
          {calc === "scf_iv" && (
            <>
              <Group grow>
                <Num label="Bias from" unit="V" value={sv.bias_min} step={0.1} onChange={(v) => setForm({ scf_iv: { ...sv, bias_min: v } })} />
                <Num label="to" unit="V" value={sv.bias_max} step={0.1} onChange={(v) => setForm({ scf_iv: { ...sv, bias_max: v } })} />
              </Group>
              <Group grow>
                <Num label="Bias points" int min={2} value={sv.n} onChange={(v) => setForm({ scf_iv: { ...sv, n: v } })} />
                <Num label="Energy step dE" unit="eV" min={0} step={0.001} value={sv.dE} onChange={(v) => setForm({ scf_iv: { ...sv, dE: v } })} />
              </Group>
              <Text size="xs" c="dimmed">Each bias is solved self-consistently (NEGF + Poisson), starting from the nearest bias already
                solved; the potential drop comes from the electrostatics, not from an assumed ramp. Gates, μ and temperature: step 3.
                Your plan: up to {String(limits["negf.max_bias_points"])} bias points.</Text>
            </>
          )}
          {calc === "ldos" && (
            <>
              <Num label="Energy" unit="eV" step={0.05} value={f.ldos.energy} onChange={(v) => setForm({ ldos: { energy: v } })} />
              <Text size="xs" c="dimmed">States per eV on each atom of the central region (summed over its orbitals and spins), with the leads attached.</Text>
            </>
          )}
          {(calc === "field_sweep" || calc === "lead_bands") && (
            <>
              <Group grow>
                <Num label="E min" unit="eV" value={f.energies.min} step={0.1} onChange={(v) => setForm({ energies: { ...f.energies, min: v } })} />
                <Num label="E max" unit="eV" value={f.energies.max} step={0.1} onChange={(v) => setForm({ energies: { ...f.energies, max: v } })} />
              </Group>
              {calc === "field_sweep"
                ? <Num label="Energy points" int min={2} value={f.energies.n} onChange={(v) => setForm({ energies: { ...f.energies, n: v } })} />
                : <Num label="k-points" int min={2} value={f.lead_bands?.nk ?? 201} onChange={(v) => setForm({ lead_bands: { nk: v } })} />}
              <Text size="xs" c="dimmed">{calc === "field_sweep"
                ? "T(E) at each field strength: Landau levels fan out with B, quantum Hall plateaus appear between them."
                : "The band structure of the lead (the device's repeated unit) with the field: Landau bands and their edge states."}</Text>
            </>
          )}
          {calc === "transfer_curve" && (
            <>
              <Group grow>
                <Num label="Vg from" unit="V" step={0.1} value={(f.transfer_curve ?? defaultTransferCurve()).Vg_start}
                  onChange={(v) => setForm({ transfer_curve: { ...(f.transfer_curve ?? defaultTransferCurve()), Vg_start: v } })} />
                <Num label="to" unit="V" step={0.1} value={(f.transfer_curve ?? defaultTransferCurve()).Vg_stop}
                  onChange={(v) => setForm({ transfer_curve: { ...(f.transfer_curve ?? defaultTransferCurve()), Vg_stop: v } })} />
              </Group>
              <Num label="Gate points" int min={2} value={(f.transfer_curve ?? defaultTransferCurve()).n_Vg}
                onChange={(v) => setForm({ transfer_curve: { ...(f.transfer_curve ?? defaultTransferCurve()), n_Vg: v } })} />
              <Num label="Drain–source bias" unit="V" step={0.01} value={(f.transfer_curve ?? defaultTransferCurve()).bias_V}
                onChange={(v) => setForm({ transfer_curve: { ...(f.transfer_curve ?? defaultTransferCurve()), bias_V: v } })} />
              <Group grow>
                <Num label="Temperature" unit="K" min={1} value={(f.transfer_curve ?? defaultTransferCurve()).temperature_K}
                  onChange={(v) => setForm({ transfer_curve: { ...(f.transfer_curve ?? defaultTransferCurve()), temperature_K: v } })} />
                <Num label="μ shift" unit="eV" step={0.05} value={(f.transfer_curve ?? defaultTransferCurve()).mu_eV}
                  onChange={(v) => setForm({ transfer_curve: { ...(f.transfer_curve ?? defaultTransferCurve()), mu_eV: v } })} />
              </Group>
              <Num label="Energy step dE" unit="eV" min={0} step={0.001} value={(f.transfer_curve ?? defaultTransferCurve()).dE_eV}
                onChange={(v) => setForm({ transfer_curve: { ...(f.transfer_curve ?? defaultTransferCurve()), dE_eV: v } })} />
              <Group grow>
                {(["x", "y", "z"] as const).map((ax, i) => (
                  <Num key={ax} label={`E/Vg·${ax}`} unit="V/Å/V" step={0.001}
                    value={(f.transfer_curve ?? defaultTransferCurve()).gate_E_per_Vg[i]}
                    onChange={(v) => {
                      const tc = f.transfer_curve ?? defaultTransferCurve();
                      const eg = [...tc.gate_E_per_Vg] as [number, number, number];
                      eg[i] = v;
                      setForm({ transfer_curve: { ...tc, gate_E_per_Vg: eg } });
                    }} />
                ))}
              </Group>
              <Text size="xs" c="dimmed">Gate via on-site E-field: U_i = −E·r_i.
                For BLG perpendicular bias Ez/Vg ≈ 0.026 V/Å/V. One Kwant device per gate point.
                Your plan: up to {String(limits["negf.max_gate_points"])} gate points.</Text>
            </>
          )}
          {(calc === "sc_conductance" || calc === "sc_map") && <ScForms sc={sc} set={setSc} limits={limits} />}
          {calc === "sc_abs" && <AbsForms sc={sc} set={setSc} />}
          {calc === "cb_map" && <CbForms sc={sc} set={setSc} limits={limits} />}
          {calc === "sc_ldos" && <Num label="Energy" unit="meV" step={0.01} value={sc.ldos?.energy_meV ?? 0}
            onChange={(v) => setSc({ ldos: { energy_meV: v } })} />}
          {isSc(calc) && <SweepForms sc={sc} set={setSc} calc={calc} limits={limits} />}
          {!isSc(calc) && <>
            <Divider label="Fields" labelPosition="left" />
            <FieldsBlock field={f.field} sweep={f.field_sweep} isSweep={calc === "field_sweep"} spinful={!!s?.spinful}
              maxFieldPoints={limits["negf.max_field_points"]}
              onField={(field) => setForm({ field })} onSweep={(field_sweep) => setForm({ field_sweep })} />
          </>}
          {status.data && !status.data.ok && <Alert color="red" title="Not available on this server">NEGF calculations need Kwant,
            which is not installed here. Please tell the administrator.</Alert>}
          {status.data?.ok && status.data.solver !== "mumps" && <Text size="xs" c="orange">This server uses SciPy's solver (slower than MUMPS).</Text>}
          {problems.map((p) => <Text key={p} size="sm" c="red">{p}</Text>)}
          <Group justify="space-between">
            <Button variant="default" onClick={onBack}>Back</Button>
            <Button onClick={() => run.mutate()} loading={run.isPending} disabled={problems.length > 0 || status.data?.ok === false}>Run</Button>
          </Group>
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <RunCard job={job} exportName={`negf_${calc}.zip`}
          empty={<Text c="dimmed" size="sm">Set the calculation and press Run: the result appears here.</Text>}>
          {job && <NegfResult job={job} />}
        </RunCard>
        <PreviousRuns project={project} kind={kind} current={shownId}
          onOpen={(id) => remember({ jobs: { ...(mem.jobs ?? {}), [calc]: id } })} describe={describeRun} />
      </Grid.Col>
    </Grid>
  );
}
